import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState } from 'react';
import { Icon } from '@iconify-icon/react';
import { Loader, useMantineTheme } from '@mantine/core';
import { Dropzone, MIME_TYPES, } from '@mantine/dropzone';
import { cn } from '@monorepo/utils';
const DropZone = ({ multiple = false, value, onChange, error, onRemoveFile, title = 'Choose a file or drag it here', maxWidth = '', accept = [
    MIME_TYPES.png,
    MIME_TYPES.jpeg,
    MIME_TYPES.pdf,
    MIME_TYPES.docx,
    MIME_TYPES.doc,
    MIME_TYPES.csv,
    MIME_TYPES.xlsx,
    MIME_TYPES.xls,
], images = [], description = 'Only txt, pdf, docx, doc, csv, xlsx files are allowed. Each file should be less than 10 MB', loading = false, maxSize, isImagePreview = false, ...props }) => {
    const theme = useMantineTheme();
    const [files, setFiles] = React.useState([]);
    const [rejectionError, setRejectionError] = useState(null);
    React.useEffect(() => {
        if (value) {
            setFiles(Array.isArray(value) ? value : [value]);
        }
    }, [value]);
    const handleFileDrop = (data) => {
        setRejectionError(null);
        if (multiple) {
            setFiles((current) => [...current, ...data]);
            onChange?.([...files, ...data]);
        }
        else {
            setFiles(data);
            onChange?.(data);
        }
    };
    const handleFileReject = (rejections) => {
        if (rejections.length > 0) {
            const { errors } = rejections[0];
            // Check if there's a file type error
            const fileTypeError = errors.find((err) => err.code === 'file-invalid-type');
            if (fileTypeError) {
                setRejectionError('Please upload a valid file type');
                return;
            }
            // Check if there's a file size error
            const fileSizeError = errors.find((err) => err.code === 'file-too-large');
            if (fileSizeError) {
                setRejectionError(`Please upload a file under the required size ${maxSize ? maxSize / 1024 ** 2 : 10}MB`);
                return;
            }
            // Generic error if none of the above
            setRejectionError(errors[0].message);
        }
    };
    // const handleFileButtonSelect = (newFiles: File | File[] | null) => {
    //   if (!newFiles) return;
    //   const filesArray = Array.isArray(newFiles) ? newFiles : [newFiles];
    //   handleFileDrop(filesArray);
    // };
    React.useEffect(() => {
        if (images.length) {
            setFiles(images);
        }
    }, [images]);
    const handleRemoveFile = React.useCallback((file, index) => {
        const newFiles = files.filter((_, i) => i !== index);
        setFiles(newFiles);
        if ('id' in file) {
            onRemoveFile?.(file.name, file.id);
        }
        else {
            onRemoveFile?.(file.name);
        }
    }, [files, onRemoveFile]);
    const truncateFileName = (fileName, maxLength = 25) => {
        if (fileName.length <= maxLength)
            return fileName;
        const extension = fileName.split('.').pop();
        const nameWithoutExt = fileName.substring(0, fileName.lastIndexOf('.'));
        const truncatedName = nameWithoutExt.substring(0, maxLength - 4);
        return `${truncatedName}...${extension ? `.${extension}` : ''}`;
    };
    const isImageFile = (file) => {
        return file.type.startsWith('image/');
    };
    return (_jsxs("div", { children: [_jsx(Dropzone, { maxFiles: 10, accept: accept, multiple: multiple, onDrop: handleFileDrop, onReject: handleFileReject, maxSize: maxSize || 10 * 1024 ** 2, classNames: {
                    inner: 'pointer-events-all h-full',
                }, ...props, className: cn(props.className, error ? 'border-red-500' : 'border-border', maxWidth), children: _jsxs("div", { className: "h-full w-auto", children: [_jsx(Dropzone.Accept, { children: _jsx(Icon, { icon: "tabler:circle-check", size: 34, color: theme.colors.green[7] }) }), _jsx(Dropzone.Reject, { children: _jsxs("div", { className: "flex h-full flex-col items-center justify-center text-center", children: [_jsx(Icon, { icon: "lucide:cloud-upload", className: "text-primary-600 text-4xl" }), _jsx("p", { className: "pt-4 font-medium text-sm text-gray-900", children: title }), _jsx("p", { className: "text-xs pt-2 text-gray-[#98A2B3] max-w-xs", children: description }), _jsx("p", { className: "text-sm text-red-500", children: rejectionError ||
                                            `Please upload a file under the required size ${maxSize ? maxSize / 1024 ** 2 : 10}MB` })] }) }), _jsx(Dropzone.Idle, { children: loading ? (_jsx("div", { className: "flex h-full items-center justify-center", children: _jsx(Loader, { size: "sm", className: "m-auto", color: "black" }) })) : (_jsxs("div", { className: "flex h-full flex-col items-center justify-center text-center", children: [_jsx(Icon, { icon: "lucide:cloud-upload", className: "text-primary-600 text-4xl" }), _jsx("p", { className: "pt-4 font-medium text-sm text-gray-900", children: title }), _jsx("p", { className: "text-xs pt-2 text-gray-[#98A2B3] max-w-xs", children: description }), _jsx("p", { className: "text-sm text-red-500", children: Array.isArray(error)
                                            ? error.map((err) => err.message).join(', ')
                                            : error?.message })] })) })] }) }), files.length > 0 && (_jsx("div", { className: "flex flex-col flex-wrap gap-4", children: _jsx("div", { className: "no-scrollbar flex w-full gap-2 overflow-auto", children: files.map((file, index) => {
                        const fileUrl = URL.createObjectURL(file);
                        const fileName = file?.name ||
                            file?.originalFileKey;
                        return (_jsxs("div", { className: "mt-2", children: [isImagePreview && isImageFile(file) ? (_jsxs("div", { className: "relative group", children: [_jsxs("div", { role: "presentation", onClick: () => window.open(fileUrl, '_blank'), className: "relative w-24 h-24 rounded-md overflow-hidden border cursor-pointer", children: [_jsx("img", { src: fileUrl, alt: fileName || 'Preview', className: "w-full h-full object-cover" }), _jsx("div", { className: "absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200" }), _jsx(Icon, { icon: "tabler:x", size: 16, className: "absolute top-1 right-1 cursor-pointer text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200", onClick: (e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        handleRemoveFile(file, index);
                                                    } })] }), _jsx("p", { className: "text-xs text-gray-600 mt-1 truncate max-w-24", children: truncateFileName(fileName || '') })] })) : (_jsxs("div", { role: "presentation", onClick: () => window.open(fileUrl, '_blank'), className: "flex w-fit cursor-pointer py-1 px-2 items-center justify-between gap-2 rounded-md border bg-white text-xs", children: [_jsx(Icon, { icon: "bx:file", size: 14, className: "text-base text-gray-800" }), _jsx("p", { className: "text-gray-800 truncate", children: truncateFileName(fileName || '') }), _jsx(Icon, { icon: "tabler:x", size: 14, className: "cursor-pointer text-base text-gray-800", onClick: (e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                handleRemoveFile(file, index);
                                            } })] })), Array.isArray(error) ? (_jsx("p", { className: "pt-1 text-xs text-red-500", children: error[index]?.message })) : (_jsx("p", { className: "pt-1 text-xs text-red-500", children: error?.message }))] }, file.path));
                    }) }) }))] }));
};
export default DropZone;
